import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { setAllItems } from '../../../../../store/slices/imageOverlaySlice';
import { saveImageItems, loadImageItems } from '../Drawing/drawingService';
import { setSaveStatus } from '../../../../../store/slices/navigationSlice';
import { useProjectHeader } from '../../../../../pages/buildProject/Helpers/pageDiv/ProjectHeaderContext';

const stripSelected = (items) => items.map(({ selected, ...rest }) => rest);

export default function ImageAutoSave() {
  const dispatch     = useDispatch();
  const items        = useSelector((s) => s.imageOverlay.items);
  const currentFloor = useSelector((s) => s.api.currentFloor);
  const projectData  = useSelector((s) => s.api.projectData);
  const floorId      = currentFloor?.enc_id ?? null;
  const projectId    = projectData?.enc_id ?? null;

  const { handleEnableDisable } = useProjectHeader();

  const baselineRef = useRef(null);
  const saveTimer   = useRef(null);
  const saveVersion = useRef(0);

  const attemptSave = (targetFloorId, toSave, serialized, version) => {
    if (version !== saveVersion.current) return;

    // Any failed save (network error, 4xx/5xx, whatever) surfaces here. We never
    // know in advance what the server will reject or why, so instead of guessing
    // a limit up front we just trust its response and roll the map back to the
    // last state we know was actually persisted.
    const handleFailure = (message) => {
      if (version !== saveVersion.current) return;
      dispatch(setSaveStatus('failed'));
      toast.error(message);
      dispatch(setAllItems(baselineRef.current ? JSON.parse(baselineRef.current) : []));
    };

    saveImageItems(targetFloorId, toSave)
      .then((result) => {
        if (version !== saveVersion.current) return;

        if (result.type === 1) {
          baselineRef.current = serialized;
          dispatch(setSaveStatus('saved'));
          setTimeout(() => dispatch(setSaveStatus('idle')), 3000);
          handleEnableDisable();
          return;
        }

        const message = result.status === 413
          ? 'Image upload failed — the file is too large. Please try a smaller image.'
          : (result.errormessage || 'Failed to save image. Please try again.');
        handleFailure(message);
      })
      .catch(() => {
        handleFailure('Failed to save image. Please try again.');
      });
  };

  useEffect(() => {
    if (!floorId) {
      baselineRef.current = null;
      return;
    }

    baselineRef.current = null;
    dispatch(setSaveStatus('idle'));

    const load = async () => {
      try {
        const saved = await loadImageItems(floorId);
        const normalized = saved?.length > 0 ? saved : [];
        baselineRef.current = JSON.stringify(stripSelected(normalized));
        dispatch(setAllItems(normalized));
      } catch {
        baselineRef.current = JSON.stringify([]);
        dispatch(setAllItems([]));
      }
    };

    load();

    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [floorId, dispatch]);

  useEffect(() => {
    if (baselineRef.current === null) return;
    if (!floorId) return;

    const toSave = stripSelected(items);
    const serialized = JSON.stringify(toSave);
    if (serialized === baselineRef.current) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);

    saveTimer.current = setTimeout(() => {
      saveVersion.current += 1;
      const version = saveVersion.current;
      dispatch(setSaveStatus('saving'));
      attemptSave(floorId, toSave, serialized, version);
    }, 1000);
  }, [items, floorId]);

  return null;
}
